/**
 * manager-search.js
 * Gerencia busca preditiva com dois modos de UI.
 * Disponível como window.SearchManager.
 *
 * ─── Modo drawer ──────────────────────────────────────────────────────────
 *
 *   O SearchManager fica DENTRO do elemento data-drawer="search".
 *   A abertura/fechamento é controlada pelo DrawerManager.
 *
 *   <!-- section search-drawer.liquid -->
 *   <div data-drawer="search" data-drawer-side="right">
 *     <div data-drawer-overlay></div>
 *     <div data-search data-search-mode="drawer">
 *       <input data-search-input type="search">
 *       <button data-search-clear hidden>×</button>
 *       <div data-search-pre-search>...</div>
 *       <div data-search-results></div>
 *     </div>
 *   </div>
 *
 * ─── Modo dropdown ────────────────────────────────────────────────────────
 *
 *   O SearchManager fica no header. O painel de resultados abre abaixo do input.
 *
 *   <div data-search data-search-mode="dropdown">
 *     <input data-search-input type="search">
 *     <button data-search-clear hidden>×</button>
 *     <div data-search-dropdown hidden>
 *       <div data-search-pre-search>...</div>
 *       <div data-search-results></div>
 *     </div>
 *   </div>
 *
 * ─── Data attributes ──────────────────────────────────────────────────────
 *
 *   data-search                → container raiz
 *   data-search-mode           → "drawer" | "dropdown"
 *   data-search-input          → campo de busca
 *   data-search-results        → container de resultados
 *   data-search-pre-search     → estado pré-busca (visível quando input vazio)
 *   data-search-clear          → botão limpar input
 *   data-search-dropdown       → painel dropdown (modo dropdown apenas)
 *   data-search-result         → item de resultado (gerado pelo manager)
 *
 * ─── API pública ──────────────────────────────────────────────────────────
 *
 *   window.SearchManager.open()
 *   window.SearchManager.close()
 *   window.SearchManager.toggle()
 *   window.SearchManager.destroy()
 */

(() => {
const STRICT_PRODUCT_SEARCH_FIELDS = [
  'title',
  'body',
  'product_type',
  'tag',
  'vendor',
  'variants.sku',
  'variants.barcode'
];
const STRICT_PRODUCT_SEARCH_STORAGE_KEY = 'theme.strictProductSearchTerms';
const STRICT_PRODUCT_SEARCH_FIELD_PATTERN = /(?:^|\(|\s)(?:title|body|product_type|tag|vendor|variants\.(?:sku|barcode)):"/;
const STRICT_COLOR_OPTION_FILTER_PARAMETER = 'filter.v.option.cor';
let strictProductSearchNavigationId = 0;
let activeStrictProductSearchMapping = null;

function normalizeColorFilterValues(values = []) {
  const valueList = Array.isArray(values) ? values : [values];
  return [...new Set(valueList.filter(value => typeof value === 'string' && value.trim()))]
    .sort((first, second) => first.localeCompare(second));
}

function getStrictProductSearchStorageKey(query, colorFilterValues = []) {
  return `${query}::${normalizeColorFilterValues(colorFilterValues).join('|')}`;
}

function getStrictProductSearchTerms() {
  try {
    return JSON.parse(window.sessionStorage.getItem(STRICT_PRODUCT_SEARCH_STORAGE_KEY) || '{}');
  } catch (error) {
    return {};
  }
}

function rememberStrictProductSearchTerm(query, originalTerm, colorFilterValues = []) {
  try {
    const terms = getStrictProductSearchTerms();
    terms[getStrictProductSearchStorageKey(query, colorFilterValues)] = originalTerm;

    const keys = Object.keys(terms);
    while (keys.length > 30) delete terms[keys.shift()];

    window.sessionStorage.setItem(STRICT_PRODUCT_SEARCH_STORAGE_KEY, JSON.stringify(terms));
  } catch (error) {
    return;
  }
}

function getOriginalStrictProductSearchTerm(query, colorFilterValues = []) {
  return getStrictProductSearchTerms()[getStrictProductSearchStorageKey(query, colorFilterValues)] || '';
}

function buildStrictProductSearchQuery(query) {
  const phrase = escapeStrictSearchPhrase(query.replace(/\s+/g, ' '));
  if (!phrase) return '';

  return `(${STRICT_PRODUCT_SEARCH_FIELDS.map(field => `${field}:"${phrase}"`).join(' OR ')})`;
}

function escapeStrictSearchPhrase(value) {
  return value.trim().replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}

function isStrictProductSearchQuery(query) {
  return STRICT_PRODUCT_SEARCH_FIELD_PATTERN.test(query.trim());
}

function isSearchUrl(url) {
  return /\/search\/?$/.test(url.pathname);
}

function normalizeSearchText(value) {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase()
    .trim()
    .replace(/\s+/g, ' ');
}

function normalizeSearchWords(value) {
  return normalizeSearchText(value).split(/[^a-z0-9]+/).filter(Boolean);
}

async function getSearchColorOptionValues(searchUrl, query) {
  const discoveryUrl = new URL(searchUrl);
  discoveryUrl.search = '';
  discoveryUrl.searchParams.set('q', query);
  discoveryUrl.searchParams.set('type', 'product');

  const response = await fetch(discoveryUrl.toString(), { credentials: 'same-origin' });
  if (!response.ok) return [];

  const searchPage = new DOMParser().parseFromString(await response.text(), 'text/html');
  const optionsElement = searchPage.querySelector('[data-search-color-options]');

  if (optionsElement) {
    try {
      const values = JSON.parse(optionsElement.textContent || '[]');
      if (Array.isArray(values)) return normalizeColorFilterValues(values);
    } catch (error) {
      // Fall through to the rendered option filter inputs.
    }
  }

  return normalizeColorFilterValues(
    [...searchPage.querySelectorAll(`input[name="${STRICT_COLOR_OPTION_FILTER_PARAMETER}"]`)]
      .map(input => input.value)
  );
}

function findSearchColorIntent(query, colorOptionValues) {
  const queryWords = normalizeSearchWords(query);
  const options = normalizeColorFilterValues(colorOptionValues).map(value => ({
    value,
    words: normalizeSearchWords(value)
  }));
  const matchedWords = queryWords.filter(word => options.some(option => option.words.includes(word)));
  if (!matchedWords.length) return null;

  const exactPhraseValues = options
    .filter(option => matchedWords.every(word => option.words.includes(word)))
    .map(option => option.value);
  const matchedValues = exactPhraseValues.length
    ? exactPhraseValues
    : options.filter(option => matchedWords.some(word => option.words.includes(word))).map(option => option.value);
  const matchedColorWords = new Set();

  matchedValues.forEach(value => {
    normalizeSearchWords(value).forEach(word => {
      if (matchedWords.includes(word)) matchedColorWords.add(word);
    });
  });

  const remainingWords = query.trim().split(/\s+/).filter(word =>
    !normalizeSearchWords(word).some(normalizedWord => matchedColorWords.has(normalizedWord))
  );
  return { values: normalizeColorFilterValues(matchedValues), remainingWords };
}

function shouldPreserveSearchParameter(name, preserveExistingFilters = false) {
  if (name === 'q' || name === 'type') return false;
  if (!preserveExistingFilters && (name === 'page' || name.startsWith('filter.'))) return false;
  return true;
}

async function navigateStrictProductSearch(searchUrl, originalTerm, extraParameters = [], preserveExistingFilters = false) {
  const navigationId = ++strictProductSearchNavigationId;
  let colorIntent = null;

  try {
    const colorOptionValues = await getSearchColorOptionValues(searchUrl, originalTerm);
    colorIntent = findSearchColorIntent(originalTerm, colorOptionValues);
  } catch (error) {
    colorIntent = null;
  }
  if (navigationId !== strictProductSearchNavigationId) return;

  const colorFilterValues = colorIntent?.values || [];
  const strictQuery = colorFilterValues.length
    ? colorIntent.remainingWords.length
      ? buildStrictProductSearchQuery(colorIntent.remainingWords.join(' '))
      : originalTerm
    : buildStrictProductSearchQuery(originalTerm);
  if (!strictQuery) return;

  const destinationUrl = new URL(searchUrl);
  const preservedParameters = new URLSearchParams();
  [...destinationUrl.searchParams.entries(), ...extraParameters].forEach(([name, value]) => {
    if (shouldPreserveSearchParameter(name, preserveExistingFilters) && typeof value === 'string') {
      preservedParameters.append(name, value);
    }
  });

  const destinationParameters = new URLSearchParams();
  const preservedEntries = [...preservedParameters.entries()];
  const pageValue = preservedEntries.find(([name]) => name === 'page')?.[1] || '1';
  destinationParameters.set('page', pageValue);
  destinationParameters.set('q', strictQuery);
  destinationParameters.set('type', 'product');
  preservedEntries.forEach(([name, value]) => {
    if (name !== 'page') destinationParameters.append(name, value);
  });

  const existingColorValues = new Set(destinationParameters.getAll(STRICT_COLOR_OPTION_FILTER_PARAMETER));
  colorFilterValues.forEach(value => {
    if (!existingColorValues.has(value)) {
      destinationParameters.append(STRICT_COLOR_OPTION_FILTER_PARAMETER, value);
    }
  });
  destinationUrl.search = destinationParameters.toString();

  rememberStrictProductSearchTerm(strictQuery, originalTerm, colorFilterValues);
  window.location.assign(destinationUrl.toString());
}

function restoreStrictProductSearchTerm() {
  const currentUrl = new URL(window.location.href);
  const currentQuery = currentUrl.searchParams.get('q') || '';
  const colorFilterValues = currentUrl.searchParams.getAll(STRICT_COLOR_OPTION_FILTER_PARAMETER);
  let queryToReplace = currentQuery;
  let originalTerm = getOriginalStrictProductSearchTerm(currentQuery, colorFilterValues);

  if (originalTerm) {
    activeStrictProductSearchMapping = { query: currentQuery, originalTerm };
  } else if (activeStrictProductSearchMapping?.originalTerm === currentQuery) {
    queryToReplace = activeStrictProductSearchMapping.query;
    originalTerm = activeStrictProductSearchMapping.originalTerm;
  }
  if (!originalTerm) return false;

  document.querySelectorAll('input[name="q"]').forEach(input => {
    if (input.type !== 'hidden' && input.value === queryToReplace) input.value = originalTerm;
  });

  const visibleRoots = [
    document.querySelector('.seoBreadcrumbs'),
    document.querySelector('.search__background'),
    document.querySelector('.collection__background')
  ].filter(Boolean);

  visibleRoots.forEach(root => {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    let textNode;
    while ((textNode = walker.nextNode())) {
      const parent = textNode.parentElement;
      if (parent?.closest('script, style, textarea')) continue;
      if (textNode.nodeValue.includes(queryToReplace)) {
        textNode.nodeValue = textNode.nodeValue.replaceAll(queryToReplace, originalTerm);
      }
    }
  });

  if (document.title.includes(queryToReplace)) {
    document.title = document.title.replaceAll(queryToReplace, originalTerm);
  }

  document.querySelectorAll('meta[name="description"], meta[property="og:description"], meta[name="twitter:description"]').forEach(meta => {
    if (meta.content.includes(queryToReplace)) meta.content = meta.content.replaceAll(queryToReplace, originalTerm);
  });

  document.getElementById('strict-search-pending-style')?.remove();

  // Keep the actual Shopify query and filters in the URL so native pagination
  // links continue using the same result set on every page.
  return true;
}

function initializeStrictProductSearch() {
  if (document.body?.dataset.strictProductSearch === 'true') {
    document.addEventListener('submit', event => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (form.id === 'CollectionFiltersForm' || form.closest('[data-drawer="filters"]')) return;

      const formUrl = new URL(form.action, window.location.href);
      const typeInput = form.querySelector('input[name="type"]');
      const queryInput = form.querySelector('input[name="q"]');
      if (!isSearchUrl(formUrl) || typeInput?.value !== 'product' || !queryInput) return;

      const originalTerm = queryInput.value.trim();
      if (!originalTerm || isStrictProductSearchQuery(originalTerm)) return;

      event.preventDefault();
      navigateStrictProductSearch(formUrl, originalTerm, [...new FormData(form).entries()]);
    }, true);

    document.addEventListener('click', event => {
      if (!(event.target instanceof Element)) return;
      const link = event.target.closest('a[href]');
      if (!link) return;
      if (link.closest('[data-collection-pagination]')) return;

      let linkUrl;
      try {
        linkUrl = new URL(link.href, window.location.href);
      } catch (error) {
        return;
      }

      if (linkUrl.origin !== window.location.origin || !isSearchUrl(linkUrl)) return;
      if (linkUrl.searchParams.get('type') !== 'product') return;

      const originalTerm = (linkUrl.searchParams.get('q') || '').trim();
      if (!originalTerm || isStrictProductSearchQuery(originalTerm) || originalTerm === 'title:*') return;

      event.preventDefault();
      navigateStrictProductSearch(linkUrl, originalTerm);
    }, true);
  }

  const restoredSearchTerm = restoreStrictProductSearchTerm();
  const currentUrl = new URL(window.location.href);
  const currentQuery = currentUrl.searchParams.get('q') || '';
  const hasColorFilter = currentUrl.searchParams.has(STRICT_COLOR_OPTION_FILTER_PARAMETER);
  const shouldResolveCleanSearch = document.body?.dataset.strictProductSearch === 'true'
    && isSearchUrl(currentUrl)
    && currentUrl.searchParams.get('type') === 'product'
    && currentQuery
    && currentQuery !== 'title:*'
    && !isStrictProductSearchQuery(currentQuery)
    && !restoredSearchTerm
    && !hasColorFilter;

  if (shouldResolveCleanSearch) {
    navigateStrictProductSearch(currentUrl, currentQuery, [], true);
  }

  if ((restoredSearchTerm || shouldResolveCleanSearch) && 'MutationObserver' in window) {
    const observer = new MutationObserver(() => restoreStrictProductSearchTerm());
    observer.observe(document.body, { childList: true, subtree: true });
  }
}

initializeStrictProductSearch();
})();

class SearchManager {
  constructor() {
    this._container = document.querySelector('[data-search]');
    if (!this._container) return;

    this._mode       = this._container.dataset.searchMode || 'dropdown';
    this._input      = this._container.querySelector('[data-search-input]');
    this._results    = this._container.querySelector('[data-search-results]');
    this._preSearch  = this._container.querySelector('[data-search-pre-search]');
    this._clearBtn   = this._container.querySelector('[data-search-clear]');
    this._dropdown   = this._container.querySelector('[data-search-dropdown]');

    this._debounceTimer = null;
    this._DEBOUNCE_MS   = 300;
    this._MIN_CHARS     = 2;
    this._RESULTS_LIMIT = 6;

    this._onInput   = this._onInput.bind(this);
    this._onKeyDown = this._onKeyDown.bind(this);
    this._onClear   = this._onClear.bind(this);

    this._init();
  }

  // ─── Inicialização ────────────────────────────────────────────────────────

  _init() {
    if (!this._input) return;

    this._input.addEventListener('input',   this._onInput);
    this._input.addEventListener('keydown', this._onKeyDown);
    this._clearBtn?.addEventListener('click', this._onClear);

    if (this._mode === 'dropdown') {
      this._initDropdown();
    } else {
      this._initDrawer();
    }
  }

  // ─── Modo Dropdown ────────────────────────────────────────────────────────

  _initDropdown() {
    // Abre ao focar no input
    this._input.addEventListener('focus', () => {
      this._showDropdown();
      this._showPreSearch();
    });

    // Fecha ao clicar fora do container
    document.addEventListener('click', e => {
      if (!this._container.contains(e.target)) this._hideDropdown();
    });

    // Fecha ao abrir qualquer drawer
    window.eventBus?.on('drawer:open', () => this._hideDropdown());
  }

  _showDropdown() {
    this._dropdown?.removeAttribute('hidden');
    this._dropdown?.classList.add('is-open');
    this._input.setAttribute('aria-expanded', 'true');
  }

  _hideDropdown() {
    this._dropdown?.setAttribute('hidden', '');
    this._dropdown?.classList.remove('is-open');
    this._input.setAttribute('aria-expanded', 'false');
  }

  // ─── Modo Drawer ──────────────────────────────────────────────────────────

  _initDrawer() {
    window.eventBus?.on('drawer:open', ({ name }) => {
      if (name !== 'search') return;
      // Foca no input apenas em desktop (evita teclado virtual no mobile)
      if (window.innerWidth >= 750) {
        requestAnimationFrame(() => this._input?.focus());
      }
      this._showPreSearch();
    });

    // Limpa resultados ao fechar o drawer
    window.eventBus?.on('drawer:close', ({ name }) => {
      if (name !== 'search') return;
      this._clearResults();
      this._showPreSearch();
      this._toggleClear(false);
      if (this._input) this._input.value = '';
    });
  }

  // ─── Busca ────────────────────────────────────────────────────────────────

  _onInput() {
    const query = this._input.value.trim();
    clearTimeout(this._debounceTimer);
    this._toggleClear(query.length > 0);

    if (query.length < this._MIN_CHARS) {
      this._clearResults();
      this._showPreSearch();
      return;
    }

    this._debounceTimer = setTimeout(() => this._fetchResults(query), this._DEBOUNCE_MS);
  }

  async _fetchResults(query) {
    try {
      const url = `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=${this._RESULTS_LIMIT}`;
      const res = await fetch(url);
      if (!res.ok) return;
      const data     = await res.json();
      const products = data.resources?.results?.products || [];
      this._renderResults(products);
    } catch (err) {
      console.error('[SearchManager] Fetch failed:', err);
    }
  }

  // ─── Renderização ─────────────────────────────────────────────────────────

  _renderResults(products) {
    this._hidePreSearch();
    if (!this._results) return;

    if (products.length === 0) {
      this._results.innerHTML = `<p class="search-manager__empty" role="status">Nenhum resultado encontrado.</p>`;
      return;
    }

    this._results.innerHTML = products.map(p => this._renderCard(p)).join('');

    // Navegação por teclado nos resultados
    const items = [...this._results.querySelectorAll('[data-search-result]')];
    items.forEach((el, i) => {
      el.addEventListener('keydown', e => {
        if (e.key === 'ArrowDown') { e.preventDefault(); items[i + 1]?.focus() ?? items[0]?.focus(); }
        if (e.key === 'ArrowUp')   { e.preventDefault(); i === 0 ? this._input.focus() : items[i - 1]?.focus(); }
        if (e.key === 'Escape')    { this._clearInput(); }
      });
    });
  }

  _renderCard(product) {
    const price = product.price
      ? (product.price / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
      : '';
    const image = product.featured_image?.url || '';

    return `
      <a href="${product.url}"
         data-search-result
         class="search-manager__result"
         tabindex="0">
        ${image
          ? `<img class="search-manager__result-image"
                  src="${image}"
                  alt="${product.title}"
                  width="60" height="60"
                  loading="lazy">`
          : ''}
        <div class="search-manager__result-info">
          <span class="search-manager__result-title">${product.title}</span>
          ${price ? `<span class="search-manager__result-price">${price}</span>` : ''}
        </div>
      </a>
    `;
  }

  // ─── Estado de UI ─────────────────────────────────────────────────────────

  _onKeyDown(e) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const first = this._results?.querySelector('[data-search-result]');
      first?.focus();
    }
    if (e.key === 'Escape') {
      this._clearInput();
      if (this._mode === 'dropdown') this._hideDropdown();
      else window.DrawerManager?.close('search');
    }
  }

  _onClear() {
    this._clearInput();
    this._input.focus();
  }

  _clearInput() {
    if (this._input) this._input.value = '';
    this._clearResults();
    this._showPreSearch();
    this._toggleClear(false);
  }

  _clearResults() {
    if (this._results) this._results.innerHTML = '';
  }

  _showPreSearch() {
    this._preSearch?.removeAttribute('hidden');
  }

  _hidePreSearch() {
    this._preSearch?.setAttribute('hidden', '');
  }

  _toggleClear(show) {
    if (!this._clearBtn) return;
    this._clearBtn.hidden = !show;
  }

  // ─── API pública ──────────────────────────────────────────────────────────

  open() {
    if (this._mode === 'drawer') window.DrawerManager?.open('search');
    else this._showDropdown();
  }

  close() {
    if (this._mode === 'drawer') window.DrawerManager?.close('search');
    else this._hideDropdown();
  }

  toggle() {
    if (this._mode === 'drawer') window.DrawerManager?.toggle('search');
    else this._dropdown?.classList.contains('is-open') ? this._hideDropdown() : this._showDropdown();
  }

  destroy() {
    this._input?.removeEventListener('input',   this._onInput);
    this._input?.removeEventListener('keydown', this._onKeyDown);
    clearTimeout(this._debounceTimer);
  }
}

document.addEventListener('DOMContentLoaded', () => {
  window.SearchManager = new SearchManager();
});
